import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaAngleLeft } from "react-icons/fa";
import { useAuth } from "context/AuthContext";
import { useUser } from "context/UserContext";
import { useLanguage } from "context/LanguageContext";
import { uuidToConsulta9 } from "../../utils/uuidConverter";
import BottomTabBar from "../../components/BottomTabBar";
import { HELP_ILLUSTRATION_URL } from "../ajustes/illustrations";
import "../payment-cards/PaymentCards.css";
import "./Soporte.css";

const WEBHOOK_URL = "https://hook.us1.make.com/y726vbfvfqskmh95vwx29m5ce7s5i4bu";

const STR = {
  es: {
    title: "Ayuda",
    subtitle: "Cuéntanos qué sucede y te responderemos lo antes posible.",
    issueTitle: "Título del problema",
    issueTitlePh: "Ej: No puedo cargar mi disponibilidad",
    issueDesc: "Descripción del problema",
    issueDescPh:
      "Describe con el mayor detalle posible qué ocurrió, en qué pantalla y si viste algún mensaje de error.",
    submit: "Enviar",
    sending: "Enviando…",
    back: "Volver",
    successTitle: "Enviado con éxito",
    successBody: "Te contactaremos a la brevedad.",
    errAuth: "No se pudo obtener el token de autenticación",
    errGeneric: "No se pudo enviar. Intenta de nuevo.",
  },
  en: {
    title: "Help",
    subtitle: "Tell us what happened and we will get back to you as soon as possible.",
    issueTitle: "Issue title",
    issueTitlePh: "E.g.: I cannot upload my availability",
    issueDesc: "Issue description",
    issueDescPh:
      "Describe in as much detail as possible what happened, which screen, and any error message you saw.",
    submit: "Submit",
    sending: "Sending…",
    back: "Back",
    successTitle: "Sent successfully",
    successBody: "We will contact you shortly.",
    errAuth: "Could not obtain authentication token",
    errGeneric: "Could not send. Please try again.",
  },
};

function SoporteSkeleton() {
  return (
    <div className="SoporteSkeleton">
      <div className="SoporteSkeletonBlock" />
      <div className="SoporteSkeletonBlock tall" />
      <div className="SoporteSkeletonBlock" />
    </div>
  );
}

export default function Soporte() {
  const { getToken } = useAuth();
  const { profesionalData, email: emailContext, documento, loading: userDataLoading } = useUser();
  const { isEn } = useLanguage();
  const t = isEn ? STR.en : STR.es;

  const [titulo, setTitulo] = useState("");
  const [detalle, setDetalle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const baseURL = process.env.REACT_APP_BASE_URL || "https://sandbox.payflexapp.com";

  useEffect(() => {
    document.documentElement.classList.add("AjustesTypographyRoot");
    return () => document.documentElement.classList.remove("AjustesTypographyRoot");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = await getToken();
    if (!token) {
      setError(t.errAuth);
      navigate("/authentication/sign-in");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      const userData = profesionalData || {};
      const userEmail = emailContext || userData.email;

      const requestData = {
        titulo,
        detalle,
        documento: documento || userData.documento,
        prioridad: "media",
        joined: [],
        archivos: [],
      };

      const response = await fetch(`${baseURL}/crear-reclamo`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let msg = t.errGeneric;
        try {
          const errorData = JSON.parse(errorText);
          msg = errorData.message || msg;
        } catch {
          /* ignore */
        }
        throw new Error(msg);
      }

      const data = await response.json();
      if (!data.reclamoId) {
        throw new Error(t.errGeneric);
      }

      const numeroConsulta = uuidToConsulta9(data.reclamoId);

      try {
        await fetch(WEBHOOK_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            mail: userEmail,
            numeroConsulta,
          }),
        });
      } catch {
        /* no bloquea éxito */
      }

      setSuccess(true);
      setTitulo("");
      setDetalle("");
    } catch (err) {
      setError(err.message || t.errGeneric);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="PaymentCardsContainer SoporteScreenRoot">
        <header className="PaymentCardsHeader">
          <button type="button" className="PaymentCardsBackButton" onClick={() => navigate("/perfil")} aria-label="back">
            <FaAngleLeft size={24} />
          </button>
          <h1>{t.title}</h1>
        </header>

        <div
          className="PaymentCardsContent SoporteScreenBody"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr",
            maxWidth: 560,
            margin: "0 auto",
            width: "100%",
          }}
        >
          <div className="SoporteIntro">
            <img
              src={HELP_ILLUSTRATION_URL}
              alt=""
              className="SoporteIntroImage"
              loading="lazy"
              decoding="async"
            />
          </div>
          <p className="SoporteSectionSubtitle">{t.subtitle}</p>

          {userDataLoading ? (
            <SoporteSkeleton />
          ) : success ? (
            <>
              <div className="SoporteSuccessBlock">
                <h2 className="SoporteSuccessTitle">{t.successTitle}</h2>
                <p className="SoporteSuccessText">{t.successBody}</p>
              </div>
              <button type="button" className="PrimaryButton SoportePrimaryWide" onClick={() => navigate("/perfil")}>
                {t.back}
              </button>
            </>
          ) : (
            <form onSubmit={handleSubmit} className="SoporteForm">
              <div className="FormGroup">
                <label htmlFor="titulo">{t.issueTitle}</label>
                <input
                  type="text"
                  id="titulo"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  placeholder={t.issueTitlePh}
                  required
                />
              </div>

              <div className="FormGroup">
                <label htmlFor="detalle">{t.issueDesc}</label>
                <textarea
                  id="detalle"
                  value={detalle}
                  onChange={(e) => setDetalle(e.target.value)}
                  placeholder={t.issueDescPh}
                  rows={6}
                  required
                />
              </div>

              {error ? <div className="ErrorMessage">{error}</div> : null}

              <button type="submit" className="PrimaryButton SoportePrimaryWide" disabled={isSubmitting}>
                {isSubmitting ? t.sending : t.submit}
              </button>
            </form>
          )}
        </div>
      </div>
      <BottomTabBar />
    </>
  );
}
